import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useClinicTestimonials } from '@/hooks/useClinicData';
import { useOptimizedNavigation } from '@/hooks/useOptimizedNavigation';
import { usePerformanceMonitor } from '@/hooks/usePerformanceMonitor';
import { LoadingSpinner } from '@/components/LoadingSpinner';

// Remove the old interface and mock data as we're using the API types now

export function TestimonialPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const { clinic, clinicLoading, clinicError, config, configLoading } = useClinicConfigContext();
  const [patientName, setPatientName] = useState<string>('');

  // Use React Query for testimonials with caching
  const {
    data: testimonialsResponse,
    isLoading: testimonialsLoading,
    error: testimonialsError,
  } = useClinicTestimonials(clinicSlug || '');

  const testimonials = testimonialsResponse?.success
    ? testimonialsResponse.data
    : [];

  // Optimized navigation with prefetching
  const { navigateWithPrefetch, handleMouseEnter } = useOptimizedNavigation();

  // Performance monitoring
  const { trackUserInteraction } = usePerformanceMonitor('TestimonialPage');
  // Theme is initialized globally in App via ThemeInitializer

  // Document title
  useDocumentTitle(clinic?.name);

  // Redirect if show_patient_review is false
  useEffect(() => {
    if (!configLoading && config) {
      const shouldShowTestimonial = config.booking_settings?.show_patient_review ?? true;
      if (!shouldShowTestimonial && clinicSlug) {
        navigate(`/${clinicSlug}/patient-service`, { replace: true });
      }
    }
  }, [config, configLoading, clinicSlug, navigate]);

  // Read patient name from cookies
  useEffect(() => {
    const getCookie = (name: string): string | null => {
      const match = document.cookie.match(
        new RegExp(
          '(?:^|; )' +
            name.replace(/([.$?*|{}()\[\\]\\\/\\+^])/g, '\\$1') +
            '=([^;]*)'
        )
      );
      return match ? decodeURIComponent(match[1]) : null;
    };
    const first = getCookie('patient_first_name');
    setPatientName(first || 'Dear Patient');
  }, []);

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  const testimonialVariants = {
    initial: { opacity: 0, y: 20, scale: 0.95 },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      y: -20,
      scale: 0.95,
      transition: { duration: 0.4, ease: 'easeIn' },
    },
  };

  if (clinicLoading || configLoading || testimonialsLoading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  // Check if testimonial should be shown
  const shouldShowTestimonial = config?.booking_settings?.show_patient_review ?? true;
  if (!shouldShowTestimonial && clinicSlug) {
    // Redirect will happen in useEffect, but show loading while redirecting
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (clinicError || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  if (testimonialsError) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>Error</h1>
          <p className='text-xl text-gray-600 mb-8'>
            {testimonialsError instanceof Error
              ? testimonialsError.message
              : 'Failed to load testimonials'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className='h-screen w-screen overflow-hidden relative flex flex-col'
      variants={pageVariants}
      initial='initial'
      animate='animate'
      exit='exit'
      transition={{ duration: 0.6 }}
    >
      {/* Main Content */}
      <motion.div
        className='flex-1 flex flex-col justify-center px-6 py-8'
        variants={staggerContainer}
        initial='initial'
        animate='animate'
      >
        <div className='max-w-4xl mx-auto w-full'>
          {/* Main Question */}
          <motion.div
            className='text-center mb-6'
            variants={fadeInUp}
            transition={{ delay: 0.3 }}
          >
            <div className='mb-24'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='48'
                height='33'
                viewBox='0 0 48 33'
                fill='none'
              >
                <g clipPath='url(#clip0_5629_21428)'>
                  <path
                    d='M21.4711 22.1979C21.6325 28.1676 16.7114 33.0525 10.6396 33.0003C4.68891 32.948 -0.0669673 27.993 0.000713495 22.1647C0.179026 7.00003 13.0735 1.50322 17.4428 0.0741968C18.2186 -0.179483 19.0555 0.238642 19.2989 1.0035L19.3054 1.02263C19.5227 1.70335 19.1921 2.43507 18.5348 2.74102C16.2116 3.8233 11.0145 6.75272 8.66516 12.1629C9.43568 12.0137 10.2387 11.9461 11.0587 11.9704C16.7192 12.1323 21.3215 16.6526 21.4724 22.1966L21.4711 22.1979Z'
                    fill='var(--clinic-text-secondary)'
                  />
                  <path
                    d='M47.9946 22.1979C48.156 28.1676 43.2348 33.0525 37.1631 33.0003C31.2123 32.948 26.4565 27.9942 26.5242 22.166C26.7025 7.00003 39.597 1.50322 43.965 0.0741968C44.7407 -0.179483 45.5776 0.238642 45.821 1.0035L45.8275 1.02263C46.0449 1.70335 45.7143 2.43507 45.057 2.74102C42.7337 3.8233 37.5366 6.75272 35.1873 12.1629C35.9578 12.0137 36.7609 11.9461 37.5808 11.9704C43.2413 12.1323 47.8436 16.6526 47.9946 22.1966V22.1979Z'
                    fill='var(--clinic-text-secondary)'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_5629_21428'>
                    <rect width='48' height='33' fill='white' />
                  </clipPath>
                </defs>
              </svg>
              <span
                className='lg:text-6xl text-4xl float-left mt-8  font-semibold pl-10'
                style={{ color: 'var(--clinic-text-secondary)' }}
              >
                {patientName}!
              </span>
            </div>
            <motion.h1
              className='text-2xl lg:text-6xl font-semibold my-4 leading-tight'
              style={{ color: 'var(--clinic-text)' }}
              initial='initial'
              animate='animate'
              transition={{ delay: 0.5 }}
            >
              {'Did you know that 80% of our patients are happy with our service?'
                .split(' ')
                .map((word, index) => (
                  <motion.span
                    key={index}
                    className='inline-block mr-2'
                    initial={{ opacity: 0, y: 20, rotateX: -90 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      rotateX: 0,
                      transition: {
                        delay: 0.8 + index * 0.1,
                        duration: 0.6,
                        ease: 'easeOut',
                      },
                    }}
                    whileHover={{
                      scale: 1.1,
                      color: 'var(--clinic-primary)',
                      transition: { duration: 0.2 },
                    }}
                  >
                    {word}
                  </motion.span>
                ))}
            </motion.h1>
            <div className='w-full flex justify-end mt-4'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='48'
                height='33'
                viewBox='0 0 48 33'
                fill='none'
              >
                <g clipPath='url(#clip0_5629_21433)'>
                  <path
                    d='M26.5289 10.8021C26.3675 4.83239 31.2886 -0.052526 37.3604 -0.00026033C43.3111 0.0520054 48.067 5.00704 47.9993 10.8353C47.821 26 34.9265 31.4968 30.5572 32.9258C29.7814 33.1795 28.9445 32.7614 28.7011 31.9965L28.6946 31.9774C28.4773 31.2966 28.8079 30.5649 29.4652 30.259C31.7884 29.1767 36.9855 26.2473 39.3348 20.8371C38.5643 20.9863 37.7613 21.0539 36.9413 21.0296C31.2808 20.8677 26.6785 16.3474 26.5276 10.8034L26.5289 10.8021Z'
                    fill='var(--clinic-text-secondary)'
                  />
                  <path
                    d='M0.00542072 10.8021C-0.155971 4.83239 4.7652 -0.052526 10.837 -0.00026033C16.7877 0.0520054 21.5435 5.00577 21.4759 10.834C21.2975 26 8.40305 31.4968 4.03503 32.9258C3.25931 33.1795 2.42241 32.7614 2.17901 31.9965L2.17251 31.9774C1.95515 31.2966 2.28574 30.5649 2.94303 30.259C5.2663 29.1767 10.4634 26.2473 12.8127 20.8371C12.0422 20.9863 11.2391 21.0539 10.4192 21.0296C4.75869 20.8677 0.1564 16.3474 0.00542072 10.8034L0.00542072 10.8021Z'
                    fill='var(--clinic-text-secondary)'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_5629_21433'>
                    <rect
                      width='48'
                      height='33'
                      fill='white'
                      transform='translate(48 33) rotate(-180)'
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </motion.div>

          {/* Testimonial Cards - Always show exactly 2 five-star cards */}
          <motion.div
            className='grid md:grid-cols-2 gap-6 mb-8'
            variants={staggerContainer}
            initial='initial'
            animate='animate'
          >
            {testimonials?.slice(0, 1).map((testimonial, index) => (
              <>
                {/* First Five-Star Testimonial Card */}
                <motion.div
                  key={`${testimonial.id}-first`}
                  className=' p-6 flex flex-col gap-4'
                  variants={testimonialVariants}
                  whileHover={{
                    scale: 1.02,
                    y: -4,
                    transition: { duration: 0.3, ease: 'easeOut' },
                  }}
                  transition={{ delay: index * 0.2 }}
                >
                  {/* Title and Subtitle */}
                  <motion.div
                    className='text-center mb-4'
                    initial='initial'
                    animate='animate'
                    transition={{ delay: 0.8 + index * 0.1 }}
                  >
                    {testimonial.subtitle && (
                      <motion.p
                        className='text-sm'
                        style={{ color: 'var(--clinic-text-secondary)' }}
                      >
                        {testimonial.subtitle}
                      </motion.p>
                    )}
                  </motion.div>

                  {/* First Testimonial */}
                  <motion.div
                    initial='initial'
                    animate='animate'
                    transition={{ delay: 0.9 + index * 0.1 }}
                  >
                    <motion.div className='flex items-start gap-2 mb-2'>
                      <div className='flex flex-col'>
                        <motion.span
                          className='text-xl font-semibold'
                          style={{ color: 'var(--clinic-text)' }}
                        >
                          {testimonial.first_five_star_name}
                        </motion.span>
                        {testimonial.first_five_star_subtitle && (
                          <motion.span
                            className='text-xl font-semibold'
                            style={{ color: 'var(--clinic-text)' }}
                          >
                            {testimonial.first_five_star_subtitle}
                          </motion.span>
                        )}
                      </div>
                    </motion.div>
                    <motion.p
                      className='text-lg'
                      style={{ color: 'var(--clinic-text)' }}
                    >
                      "{testimonial.first_five_star_tm}"
                    </motion.p>
                    <div className='flex justify-start items-center gap-1 mt-1'>
                      {[...Array(5)].map((_, starIndex) => (
                        <motion.svg
                          key={starIndex}
                          className='w-8 h-8 text-[#ED8A19]'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{
                            opacity: 1,
                            scale: 1,
                            transition: {
                              delay: 1.0 + index * 0.1 + starIndex * 0.1,
                              duration: 0.3,
                              ease: 'easeOut',
                            },
                          }}
                        >
                          <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                        </motion.svg>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>

                {/* Second Five-Star Testimonial Card */}
                <motion.div
                  key={`${testimonial.id}-second`}
                  className='p-6 flex flex-col gap-4'
                  variants={testimonialVariants}
                  whileHover={{
                    scale: 1.02,
                    y: -4,
                    transition: { duration: 0.3, ease: 'easeOut' },
                  }}
                  transition={{ delay: (index + 0.5) * 0.2 }}
                >
                  {/* Title and Subtitle */}
                  <motion.div
                    className='text-center mb-4'
                    initial='initial'
                    animate='animate'
                    transition={{ delay: 0.8 + (index + 0.5) * 0.1 }}
                  >
                    {testimonial.subtitle && (
                      <motion.p
                        className='text-sm'
                        style={{ color: 'var(--clinic-text)' }}
                      >
                        {testimonial.subtitle}
                      </motion.p>
                    )}
                  </motion.div>

                  {/* Second Testimonial */}
                  <motion.div
                    initial='initial'
                    animate='animate'
                    transition={{ delay: 0.9 + (index + 0.5) * 0.1 }}
                  >
                    <motion.div className='flex items-start gap-2 mb-2'>
                      <div className='flex flex-col'>
                        <motion.span
                          className='text-xl font-semibold'
                          style={{ color: 'var(--clinic-text)' }}
                        >
                          {testimonial.second_five_star_name}
                        </motion.span>
                        {testimonial.second_five_star_subtitle && (
                          <motion.span
                            className='text-xl font-semibold'
                            style={{ color: 'var(--clinic-text)' }}
                          >
                            {testimonial.second_five_star_subtitle}
                          </motion.span>
                        )}
                      </div>
                    </motion.div>
                    <motion.p
                      className='text-lg'
                      style={{ color: 'var(--clinic-text)' }}
                    >
                      "{testimonial.second_five_star_tm}"
                    </motion.p>
                    <div className='flex justify-start items-center gap-1 mt-1'>
                      {[...Array(5)].map((_, starIndex) => (
                        <motion.svg
                          key={starIndex}
                          className='w-8 h-8 text-[#ED8A19]'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{
                            opacity: 1,
                            scale: 1,
                            transition: {
                              delay:
                                1.0 + (index + 0.5) * 0.1 + starIndex * 0.1,
                              duration: 0.3,
                              ease: 'easeOut',
                            },
                          }}
                        >
                          <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                        </motion.svg>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>
              </>
            ))}
          </motion.div>

          {/* Next Button */}
          <motion.div
            className='flex justify-center'
            variants={fadeInUp}
            transition={{ delay: 1.5 }}
          >
            <motion.button
              onClick={() => {
                trackUserInteraction('testimonial_continue_clicked');
                navigateWithPrefetch(
                  `/${clinicSlug}/patient-service`,
                  clinicSlug || '',
                  'all'
                );
              }}
              onMouseEnter={() => {
                if (clinicSlug) {
                  handleMouseEnter(clinicSlug, 'all');
                }
              }}
              className='h-[54px] min-w-[300px] px-6 py-3 rounded-xl text-base transition-all duration-300 flex items-center justify-center gap-2.5 shadow-md font-semibold hover:opacity-90'
              style={{
                backgroundColor: 'var(--clinic-secondary)',
                color: 'var(--clinic-text)',
              }}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.3 }}
            >
              Next
            </motion.button>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}
